const parseMoney = (value) => {
  const n = Number(String(value ?? "").trim() || 0);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Number(n.toFixed(2)));
};

const parseQty = (value) => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return 0;
  return n;
};

const toCents = (value) => Math.round(parseMoney(value) * 100);
const fromCents = (value) => Number((Number(value || 0) / 100).toFixed(2));

const normalizeModeValue = (value) => String(value || "").trim().toLowerCase();

export const parseSplitPayment = (raw) => {
  if (!raw) return null;
  let obj = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== "object") return null;
  const cash = parseMoney(obj.cash);
  const upi = parseMoney(obj.upi);
  const card = parseMoney(obj.card);
  const credit = parseMoney(obj.credit);
  const total = parseMoney(obj.total ?? cash + upi + card + credit);
  const methods = Array.isArray(obj.methods) ? obj.methods : [];
  return { cash, upi, card, credit, total, methods };
};

export const normalizePaymentMode = (mode) => {
  const raw = normalizeModeValue(mode);
  if (!raw) return "cash";
  if (raw.includes("split")) return "split";
  if (raw.includes("upi")) return "upi";
  if (raw.includes("card")) return "card";
  if (
    raw.includes("credit") ||
    raw.includes("udhar") ||
    raw.includes("party") ||
    raw.includes("customer")
  ) {
    return "credit";
  }
  if (raw.includes("cash")) return "cash";
  return "cash";
};

export const getOrderPaymentMode = (order) =>
  normalizePaymentMode(order?.payment_mode || order?.paymentmode || order?.mode);

export const buildPaymentMethodReportFromOrders = (orders, dateStr, restaurantName) => {
  const totalsCents = { cash: 0, upi: 0, card: 0, credit: 0 };
  const orderCounts = { cash: 0, upi: 0, card: 0, credit: 0 };
  const qtyTotals = { cash: 0, upi: 0, card: 0, credit: 0 };
  let totalAmountCents = 0;
  let totalCreditCents = 0;
  let totalQty = 0;

  (orders || []).forEach((order) => {
    const qty = parseQty(order?.totalqty);
    totalQty += qty;
    const split = parseSplitPayment(order?.split_payment || order?.splitPayment);

    if (split && split.total > 0) {
      const splitTotalCents = toCents(split.total);
      totalAmountCents += splitTotalCents > 0 ? splitTotalCents : toCents(order?.totalamount);
      totalCreditCents += toCents(split.credit);
      const parts = [
        { key: "cash", amount: split.cash },
        { key: "upi", amount: split.upi },
        { key: "card", amount: split.card },
        { key: "credit", amount: split.credit },
      ];
      parts.forEach((part) => {
        if (!part.amount) return;
        const amountCents = toCents(part.amount);
        if (amountCents <= 0) return;
        totalsCents[part.key] += amountCents;
        orderCounts[part.key] += 1;
        if (splitTotalCents > 0 && qty > 0) {
          qtyTotals[part.key] += qty * (amountCents / splitTotalCents);
        }
      });
      return;
    }

    const mode = getOrderPaymentMode(order);
    const amountCents = toCents(order?.totalamount);
    totalAmountCents += amountCents;
    if (totalsCents[mode] !== undefined) {
      totalsCents[mode] += amountCents;
      orderCounts[mode] += 1;
      qtyTotals[mode] += qty;
      if (mode === "credit") totalCreditCents += amountCents;
    }
  });

  const totalAmount = fromCents(totalAmountCents);
  const modes = ["cash", "upi", "card", "credit"].map((key) => {
    const amount = fromCents(totalsCents[key]);
    const share = totalAmount > 0 ? Math.round((amount / totalAmount) * 100) : 0;
    return {
      mode: key,
      total_orders: orderCounts[key] || 0,
      total_quantity: Number((qtyTotals[key] || 0).toFixed(2)),
      total_amount: amount,
      share,
    };
  });

  return {
    date: dateStr,
    restaurant: restaurantName || "",
    total_orders: (orders || []).length,
    total_quantity: Number(totalQty.toFixed(2)),
    total_amount: totalAmount,
    total_credit_amount: fromCents(totalCreditCents),
    modes,
  };
};

export const buildSplitReportFromOrders = (orders, dateStr, restaurantName) => {
  const totalsCents = { cash: 0, upi: 0, card: 0, credit: 0 };
  const modeCounts = { cash: 0, upi: 0, card: 0, credit: 0 };
  const splitOrders = [];
  let totalSplitCents = 0;

  (orders || []).forEach((order) => {
    const split = parseSplitPayment(order?.split_payment || order?.splitPayment);
    if (!split || split.total <= 0) return;
    const splitTotalCents = toCents(split.total);
    totalSplitCents += splitTotalCents;
    const parts = [
      { key: "cash", amount: split.cash },
      { key: "upi", amount: split.upi },
      { key: "card", amount: split.card },
      { key: "credit", amount: split.credit },
    ];
    parts.forEach((part) => {
      if (!part.amount) return;
      const amountCents = toCents(part.amount);
      if (amountCents <= 0) return;
      totalsCents[part.key] += amountCents;
      modeCounts[part.key] += 1;
    });

    splitOrders.push({
      id: order?.id,
      ordercode: order?.ordercode || order?.id,
      created_at: order?.created_at,
      totalamount: parseMoney(order?.totalamount || split.total),
      partyid: order?.partyid ?? null,
      tableid: order?.tableid ?? null,
      payment_mode: order?.payment_mode || order?.paymentmode || order?.mode,
      split: {
        cash: split.cash,
        upi: split.upi,
        card: split.card,
        credit: split.credit,
        total: split.total,
      },
    });
  });

  return {
    date: dateStr,
    restaurant: restaurantName || "",
    total_split_orders: splitOrders.length,
    total_split_amount: fromCents(totalSplitCents),
    totals_by_mode: {
      cash: fromCents(totalsCents.cash),
      upi: fromCents(totalsCents.upi),
      card: fromCents(totalsCents.card),
      credit: fromCents(totalsCents.credit),
    },
    mode_order_counts: modeCounts,
    orders: splitOrders,
  };
};
